import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";
import { MessageSquareWarning, Plus, Sparkles, Loader2 } from "lucide-react";

const statusColor = {
  open: "bg-amber-500/20 text-amber-300",
  in_review: "bg-sky-500/20 text-sky-300",
  resolved: "bg-emerald-500/20 text-emerald-300",
};

export default function Feedback() {
  const { user } = useAuth();
  const isMgr = user?.role === "manager" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [form, setForm] = useState({ type: "suggestion", title: "", body: "", anonymous: false });
  const [note, setNote] = useState({});

  const load = async () => setItems((await api.get("/feedback")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/feedback", form);
      toast.success("Submitted. Thank you for speaking up.");
      setOpen(false); setForm({ type: "suggestion", title: "", body: "", anonymous: false });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (f, status) => { await api.patch(`/feedback/${f.id}`, { status }); load(); };
  const addNote = async (f) => {
    if (!note[f.id]?.trim()) return;
    await api.patch(`/feedback/${f.id}`, { progress_note: note[f.id] });
    setNote({ ...note, [f.id]: "" });
    toast.success("Progress note added.");
    load();
  };
  const summarize = async (f) => {
    setAiBusy(f.id);
    try {
      await api.post(`/feedback/${f.id}/summarize`);
      toast.success("AI summary generated.");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setAiBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquareWarning className="w-5 h-5 text-emerald-400" />
          <h1 className="font-display text-2xl font-bold">{isMgr ? "Complaints & Suggestions" : "My feedback"}</h1>
        </div>
        {!isMgr && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="new-feedback-btn"><Plus className="w-4 h-4 mr-1" /> New submission</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121816] border-emerald-500/20">
              <DialogHeader><DialogTitle>Share a complaint or suggestion</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="feedback-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121816] border-emerald-500/20">
                      <SelectItem value="suggestion">Suggestion</SelectItem>
                      <SelectItem value="complaint">Complaint</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="feedback-title-input" /></div>
                <div><Label>Details</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="feedback-body-input" /></div>
                <div className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2">
                  <Label htmlFor="anon" className="cursor-pointer">Submit anonymously</Label>
                  <Switch id="anon" checked={form.anonymous} onCheckedChange={(v) => setForm({ ...form, anonymous: v })} data-testid="feedback-anon-switch" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={busy || !form.title || !form.body} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="feedback-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="card-emerald p-12 text-center text-muted-foreground">Nothing here yet.</div>
      ) : (
        <div className="space-y-4">
          {items.map((f) => (
            <div key={f.id} className="card-emerald p-5" data-testid="feedback-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-emerald-500/30 capitalize">{f.type}</Badge>
                    <Badge className={statusColor[f.status]}>{f.status.replace("_", " ")}</Badge>
                  </div>
                  <h3 className="font-semibold mt-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{f.body}</p>
                  <p className="text-xs text-muted-foreground mt-2">by {f.author_name} · {fmtDateTime(f.created_at)}</p>
                </div>
              </div>

              {f.ai_summary && (
                <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-xs font-semibold text-emerald-300 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> AI summary</p>
                  <p className="text-sm mt-1 text-emerald-100/90">{f.ai_summary}</p>
                </div>
              )}

              {f.progress_notes?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {f.progress_notes.map((n, i) => (
                    <p key={i} className="text-xs text-muted-foreground border-l-2 border-emerald-500/30 pl-2">
                      <span className="text-foreground">{n.by}:</span> {n.note}
                    </p>
                  ))}
                </div>
              )}

              {isMgr && (
                <div className="mt-4 pt-4 border-t border-white/5 flex flex-col sm:flex-row gap-2">
                  <Select value={f.status} onValueChange={(v) => setStatus(f, v)}>
                    <SelectTrigger className="w-full sm:w-40 bg-[#0A0D0C] border-emerald-500/20" data-testid="feedback-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121816] border-emerald-500/20">
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_review">In review</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Add progress note…" value={note[f.id] || ""} onChange={(e) => setNote({ ...note, [f.id]: e.target.value })}
                    className="flex-1 bg-[#0A0D0C] border-emerald-500/20" data-testid="feedback-note-input" />
                  <Button variant="outline" className="border-emerald-500/30" onClick={() => addNote(f)} data-testid="feedback-note-btn">Add</Button>
                  <Button variant="outline" className="border-emerald-500/30" onClick={() => summarize(f)} disabled={aiBusy === f.id} data-testid="feedback-summarize-btn">
                    {aiBusy === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> AI</>}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
